import compact from 'lodash/compact';
import isEmpty from 'lodash/isEmpty';

import { LoggerScope } from '~/main/utils/logger';
import Logger from '~/renderer/utils/logger';
import { keys, RecursivePartial } from '~/shared';

import { getBrowserLocale } from '.';

interface LocalStorage {
  background: string;
  logo: string;
  i18nextLng: string;
  options: {
    locale: string;
    accountId: string;
    registerId: string;
    shopId: string;
    shopName: string;
    userId: string;
    currencyCode: string;
    dateTimeFormat: string;
    isCustomerNameVisible: string | boolean;
    isUntaxedItemNoteVisible: string | boolean;
    isQRCodeVisible: string | boolean;
    isConnectionStatusVisible: string | boolean;
    isDebugMode: string | boolean;
    hubBackendEnvironment: string;
  };
}

export const localStorageMapping = {
  accountId: 'OPTIONS.accountId',
  shopId: 'OPTIONS.shopId',
  shopName: 'OPTIONS.shopName',
  background: 'BACKGROUND',
  logo: 'LOGO',
  i18nextLng: 'i18nextLng',
  locale: 'OPTIONS.locale',
  currencyCode: 'OPTIONS.currencyCode',
  dateTimeFormat: 'OPTIONS.dateTimeFormat',
  isCustomerNameVisible: 'OPTIONS.isCustomerNameVisible',
  isUntaxedItemNoteVisible: 'OPTIONS.isUntaxedItemNoteVisible',
  isQRCodeVisible: 'OPTIONS.isQRCodeVisible',
  isConnectionStatusVisible: 'OPTIONS.isConnectionStatusVisible',
  isDebugMode: 'OPTIONS.isDebugMode',
  options: 'OPTIONS',
  hubBackendEnvironment: 'OPTIONS.hubBackendEnvironment',
} as const;

export const schema: LocalStorage = {
  options: {
    accountId: 'accountId',
    registerId: 'registerId',
    shopId: 'shopId',
    shopName: 'shopName',
    userId: 'userId',
    locale: 'locale',
    currencyCode: 'currencyCode',
    dateTimeFormat: 'dateTimeFormat',
    isCustomerNameVisible: 'isCustomerNameVisible',
    isUntaxedItemNoteVisible: 'isUntaxedItemNoteVisible',
    isQRCodeVisible: 'isQRCodeVisible',
    isConnectionStatusVisible: 'isConnectionStatusVisible',
    isDebugMode: 'isDebugMode',
    hubBackendEnvironment: 'hubBackendEnvironment',
  },
  background: 'background',
  logo: 'logo',
  i18nextLng: 'i18nextLng',
};

const base = keys(schema);
const op = keys(schema.options);
const isBaseKey = (key: string): key is keyof LocalStorage =>
  base.includes(key as keyof LocalStorage);
const isOptionsKey = (key: string): key is keyof LocalStorage['options'] =>
  op.includes(key as keyof LocalStorage['options']);

export type Config = Pick<LocalStorage, 'background' | 'logo' | 'i18nextLng' | 'options'>;

export const getConfig = (
  keys: Array<keyof LocalStorage> = ['background', 'logo', 'i18nextLng', 'options'],
): Partial<Config> =>
  keys.reduce((config, key) => {
    const value =
      (localStorageMapping[key] && localStorage.getItem(localStorageMapping[key])) || '';
    let options;

    try {
      if (key === 'options') {
        options = JSON.parse(String(value || '{}'));
      }
    } catch (err) {
      Logger({ scope: LoggerScope.HUB }).error({ err });
    }

    return Object.assign({}, config, {
      [key]: options || value,
    });
  }, {});

export const setConfig = (config: RecursivePartial<LocalStorage>) => {
  const options = {
    ...(config as Partial<LocalStorage>).options,
  };
  const flattenedConfig = config as LocalStorage & LocalStorage['options'];

  keys(flattenedConfig).forEach(key => {
    const value = flattenedConfig[key] as string;

    if (isBaseKey(key) && key !== 'options') {
      localStorage.setItem(localStorageMapping[key], value);
    } else if (isOptionsKey(key)) {
      options[key] = value;
    }
  });

  if (!isEmpty(options)) {
    localStorage.setItem(
      'OPTIONS',
      JSON.stringify(
        Object.assign({}, JSON.parse(String(localStorage.getItem('OPTIONS'))) || {}, options),
      ),
    );
  }
};

export const clearConfig = () => {
  localStorage.clear();
};

export const isDebugMode = () => typeof window !== 'undefined' && !!getDebugMode();

export const getDebugMode = () => {
  const { options } = getConfig(['options']);
  return options?.isDebugMode ? 3 : 0;
};

export const getLocale = () => {
  const { options, i18nextLng } = getConfig(['options', 'i18nextLng']);
  let locale = options?.locale || getBrowserLocale();

  if (i18nextLng) {
    locale = compact([i18nextLng.slice(0, 2), locale?.slice(-2)]).join('-');
  }

  return locale;
};

export const sanitizeLogo = (rawUrl: string) => {
  let url = rawUrl;
  // Limitation due to JIMP library used for analyzing pixels
  const allowedExtensions = ['jpg', 'png', 'gif'];
  const extension = getExtension(rawUrl);

  if (extension && !allowedExtensions.includes(extension)) {
    url = url.replace(`.${extension}`, '.png');
  }

  return url;
};

export const getExtension = (url: string) => {
  const [, extension] = url.match(/\.(.{3,4})(?:\?.*)?$/) || [];
  return extension;
};
